import { memo, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Avatar, Button, Modal, ModalBody, ModalContent, ModalFooter } from '@heroui/react';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import {
  CHAT_GROUP_AVATAR_ACCEPT,
  CHAT_GROUP_MAX_PARTICIPANTS,
  CHAT_MODAL_WIDTH_CLASS,
  CHAT_USERNAME_SEARCH_MAX,
} from '../../config/constants';
import type { ChatUser } from '../../model/types';
import {
  IconCamera,
  IconCheckCircle,
  IconChevronDown,
  IconPlus,
  IconSearch,
  IconUpload,
  IconUser,
  IconUsers,
} from '../icons';

export type CreateGroupChatModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onTitleChange: (value: string) => void;
  avatarFile: File | null;
  avatarPreviewUrl: string | null;
  onAvatarFileChange: (file: File | null) => void;
  users: ChatUser[];
  search: string;
  onSearchChange: (value: string) => void;
  selectedUserIds: number[];
  onToggleUser: (userId: number) => void;
  isLoadingUsers: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: () => void | Promise<void>;
};

export const CreateGroupChatModal = memo(
  ({
    isOpen,
    onClose,
    title,
    onTitleChange,
    avatarFile,
    avatarPreviewUrl,
    onAvatarFileChange,
    users,
    search,
    onSearchChange,
    selectedUserIds,
    onToggleUser,
    isLoadingUsers,
    isSubmitting,
    errorMessage,
    onSubmit,
  }: CreateGroupChatModalProps) => {
    useModalAutoFocus(isOpen);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [participantsExpanded, setParticipantsExpanded] = useState(false);
    const [avatarError, setAvatarError] = useState<string | null>(null);

    const canSave = title.trim().length > 0 && !!avatarFile && !isSubmitting;

    const selectedCountLabel = useMemo(() => {
      const count = selectedUserIds.length;
      if (!count) return null;
      if (count === 1) return '1 participant selected';
      return `${count} participants selected`;
    }, [selectedUserIds.length]);

    useEffect(() => {
      if (!isOpen) {
        setParticipantsExpanded(false);
        setAvatarError(null);
      }
    }, [isOpen]);

    const handleClose = () => {
      if (isSubmitting) return;
      onClose();
    };

    const handleAvatarPick = (file: File | null) => {
      setAvatarError(null);

      if (!file) {
        onAvatarFileChange(null);
        return;
      }

      if (!file.type.startsWith('image/')) {
        setAvatarError('Only PNG or JPG images are allowed');
        return;
      }

      onAvatarFileChange(file);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        placement="center"
        backdrop="opaque"
        hideCloseButton={isSubmitting}
        classNames={{
          base: `${CHAT_MODAL_WIDTH_CLASS} rounded-[28px] p-0`,
          closeButton: 'top-7 right-7 text-black/50 hover:bg-transparent hover:text-black',
        }}
      >
        <ModalContent className="overflow-hidden p-0">
          <ModalBody className="px-9 pb-0 pt-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'relative flex h-[120px] w-[120px] items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-[#D0D0D0] bg-[#FAFAFA] transition-colors hover:border-[#27245F]',
                    avatarPreviewUrl && 'border-solid border-transparent',
                  )}
                  aria-label="Upload group avatar"
                >
                  {avatarPreviewUrl ? (
                    <>
                      <img
                        src={avatarPreviewUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/35">
                        <IconCamera className="text-white" />
                      </span>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-2 px-3 text-center text-grey-secondary">
                      <IconUpload className="text-[#27245F]" />
                      <span className="text-[13px] leading-tight">
                        PNG, JPG
                        <br />
                        (max 1024x1024px)
                      </span>
                    </div>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={CHAT_GROUP_AVATAR_ACCEPT}
                  className="hidden"
                  onChange={(event) => {
                    handleAvatarPick(event.target.files?.[0] ?? null);
                    event.target.value = '';
                  }}
                />
                {avatarError ? <p className="text-sm text-danger">{avatarError}</p> : null}
              </div>

              <label className="flex flex-col gap-2">
                <span className="text-[15px] font-medium text-[#1E1E1E]">Group name</span>
                <input
                  value={title}
                  onChange={(event) => onTitleChange(event.target.value)}
                  placeholder="Enter group name"
                  className="h-[52px] rounded-2xl border border-[#E4E4E4] px-4 text-base text-[#1E1E1E] outline-none placeholder:text-grey focus:border-[#27245F]"
                />
              </label>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => setParticipantsExpanded((value) => !value)}
                  className="flex w-full items-center justify-between py-1 text-left"
                >
                  <span className="flex items-center gap-2 text-[16px] font-medium text-[#1E1E1E]">
                    <IconUsers className="text-[#27245F]" />
                    Add participants
                    {selectedCountLabel ? (
                      <span className="text-[13px] font-normal text-grey-secondary">
                        ({selectedUserIds.length}/{CHAT_GROUP_MAX_PARTICIPANTS})
                      </span>
                    ) : null}
                  </span>
                  <IconChevronDown
                    className={cn(
                      'text-[#1E1E1E] transition-transform',
                      participantsExpanded && 'rotate-180',
                    )}
                  />
                </button>

                {participantsExpanded ? (
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3 rounded-2xl bg-[#F6F6F6] px-4 py-4">
                      <IconSearch className="shrink-0 text-grey-secondary" />
                      <input
                        value={search}
                        maxLength={CHAT_USERNAME_SEARCH_MAX}
                        onChange={(event) => onSearchChange(event.target.value)}
                        aria-label="Search participants"
                        placeholder="Username in academic list, or exact ID / name"
                        className="w-full bg-transparent text-base text-[#1E1E1E] outline-none placeholder:text-grey"
                      />
                    </div>

                    <div className="max-h-[280px] overflow-y-auto pr-2">
                      <ul className="flex flex-col gap-2">
                        {users.map((user) => {
                          const isSelected = selectedUserIds.includes(user.id);
                          const atLimit =
                            !isSelected && selectedUserIds.length >= CHAT_GROUP_MAX_PARTICIPANTS;
                          const fullName =
                            [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
                            user.username;

                          return (
                            <li key={user.id}>
                              <button
                                type="button"
                                disabled={atLimit}
                                onClick={() => onToggleUser(user.id)}
                                className={cn(
                                  'flex w-full items-center gap-4 rounded-3xl px-4 py-3 text-left transition-colors',
                                  isSelected ? 'bg-[#F1F1F1]' : 'bg-white hover:bg-[#F7F7F7]',
                                  atLimit && 'cursor-not-allowed opacity-50',
                                )}
                              >
                                <Avatar
                                  name={fullName}
                                  src={user.avatarUrl ?? undefined}
                                  size="md"
                                  showFallback
                                  className="h-11 w-11 shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 text-[#1E1E1E]">
                                    <IconUser className="shrink-0" />
                                    <span className="truncate text-[15px] font-medium">
                                      {fullName}
                                    </span>
                                  </div>
                                  {user.badge ? (
                                    <span className="mt-0.5 block truncate pl-6 text-[13px] text-grey-secondary">
                                      {user.badge}
                                    </span>
                                  ) : (
                                    <span className="mt-0.5 block truncate pl-6 text-[13px] text-grey-secondary">
                                      @{user.username}
                                    </span>
                                  )}
                                </div>
                                {isSelected ? (
                                  <IconCheckCircle className="shrink-0 text-[#27245F]" />
                                ) : null}
                              </button>
                            </li>
                          );
                        })}
                      </ul>

                      {!isLoadingUsers && !users.length ? (
                        <div className="py-8 text-center text-sm text-default-500">
                          {search.trim()
                            ? 'No matching users'
                            : 'Browse academic list or search to add participants'}
                        </div>
                      ) : null}

                      {isLoadingUsers ? (
                        <div className="py-8 text-center text-sm text-default-500">Loading...</div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              {errorMessage ? <p className="text-sm text-danger">{errorMessage}</p> : null}
            </div>
          </ModalBody>
          <ModalFooter className="mt-8 flex justify-between gap-5 border-t border-[#ECECEC] px-9 py-6">
            <Button
              variant="bordered"
              className="h-[52px] min-w-[230px] rounded-[12px] border-[#1E1E1E] bg-white text-[16px] font-medium text-[#1E1E1E]"
              isDisabled={isSubmitting}
              onPress={handleClose}
            >
              Cancel
            </Button>
            <Button
              className={cn(
                'h-[52px] min-w-[230px] rounded-[12px] text-[16px] font-medium text-white',
                canSave ? 'bg-[#27245F]' : 'bg-[#C8C8C8]',
              )}
              startContent={<IconPlus />}
              isDisabled={!canSave}
              isLoading={isSubmitting}
              onPress={() => void onSubmit()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);

CreateGroupChatModal.displayName = 'CreateGroupChatModal';
